import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceConfig, SinglePriceConfigType } from '../../types/SinglePriceContent';
import { ModelContainer, ModelPrice, LegalTextContainer } from './DealerPriceInfo.style';
import {
  PriceWrapper,
  ShortDisclaimer,
  PricesContainer,
  SecondaryPrice,
  ModelPriceLabel,
} from './PriceInfo-T3.style';
import { formatPrice } from '../../utils/currencyTools';
import { CarPricesType } from '../../types/oneGraphTypes';

interface PriceInfoT3Props {
  priceConfig: SinglePriceConfigType;
  carPrices: CarPricesType[];
  carType: string;
}

const PriceInfoT3: React.FC<PriceInfoT3Props> = ({ priceConfig, carPrices, carType }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const isFrench = language === 'fr';
  const vehicleType = carType === 'N' ? 'new' : 'used';

  const getPriceConfig = useCallback(
    (type: string) => {
      if (type === 'new') return priceConfig?.newVehiclePrice ?? [];
      return [priceConfig?.usedVehiclePrice ?? {}];
    },
    [priceConfig?.newVehiclePrice, priceConfig?.usedVehiclePrice],
  );

  const getPriceDetails = useCallback(
    (priceArray: CarPricesType[], key: string, textOverride: string | undefined) => {
      const priceAmount = priceArray?.find((item) => item?.type === key)?.price?.value;
      if (priceAmount) return formatPrice(priceAmount, isFrench);
      return textOverride?.length ? textOverride : t('vehiclePricingBar.textOverrideDefault');
    },
    [isFrench, t],
  );

  const isDualPrice = useMemo(
    () => vehicleType === 'new' && priceConfig?.newVehiclePrice?.length > 1,
    [priceConfig?.newVehiclePrice?.length, vehicleType],
  );

  const [{ label = '', shortDisclaimer = '', longDisclaimer = '', priceKey = '', textOverride }] =
    useMemo(() => getPriceConfig(vehicleType), [getPriceConfig, vehicleType]);

  const priceAmount = useMemo(
    () => getPriceDetails(carPrices, priceKey, textOverride),
    [getPriceDetails, carPrices, priceKey, textOverride],
  );

  const secondPriceDetails = useMemo(() => {
    // Skip first value and get Second Price
    const [, secondPriceConfig = {} as PriceConfig] = priceConfig?.newVehiclePrice ?? [];
    const key = secondPriceConfig?.priceKey ?? '';
    const textOver = secondPriceConfig?.textOverride;
    const result = {
      amount: getPriceDetails(carPrices, key, textOver) ?? '—',
      label: secondPriceConfig?.label ?? '',
      shortDisclaimer: secondPriceConfig?.shortDisclaimer ?? '',
      longDisclaimer: secondPriceConfig?.longDisclaimer ?? '',
    };
    return result;
  }, [getPriceDetails, priceConfig?.newVehiclePrice, carPrices]);

  return (
    <ModelContainer>
      <PricesContainer>
        <PriceWrapper>
          <ModelPriceLabel data-testid="price-label">
            {label}:
            <ModelPrice data-vehicle-price={priceAmount} data-testid="model-price">
              {priceAmount}
              {!!longDisclaimer?.length && '*'}
            </ModelPrice>
          </ModelPriceLabel>
          <ShortDisclaimer data-testid="first-short-disclaimer">{shortDisclaimer}</ShortDisclaimer>
        </PriceWrapper>
        {isDualPrice && (
          <PriceWrapper>
            <ModelPriceLabel data-testid="second-price-label">
              {secondPriceDetails.label}:
              <SecondaryPrice
                data-vehicle-price={secondPriceDetails.amount}
                data-testid="second-model-price"
              >
                {secondPriceDetails.amount}
                {secondPriceDetails.longDisclaimer.length > 0 && '**'}
              </SecondaryPrice>
            </ModelPriceLabel>
            <ShortDisclaimer data-testid="second-short-disclaimer">
              {secondPriceDetails?.shortDisclaimer}
            </ShortDisclaimer>
          </PriceWrapper>
        )}
      </PricesContainer>
      <LegalTextContainer data-testid="price-legal-text">
        {!!longDisclaimer?.length && `*${longDisclaimer}`}
        {isDualPrice && secondPriceDetails.longDisclaimer.length > 0 && (
          <>
            {!!longDisclaimer?.length && <br />}
            **{secondPriceDetails.longDisclaimer}
          </>
        )}
      </LegalTextContainer>
    </ModelContainer>
  );
};

export default PriceInfoT3;
